/**
 * Employee Edit Modal Component
 *
 * Modal form for adding or editing employee details.
 * Uses react-hook-form with zod validation.
 *
 * Session 4.2: Step 3 - Map & Validate
 */

import { useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { WizardEmployee } from '../context/types';
import { useWizard } from '../context/WizardContext';

// =============================================================================
// VALIDATION SCHEMA
// =============================================================================

const employeeSchema = z.object({
  employeeNi: z
    .string()
    .min(8, 'NI# must be 8 digits')
    .max(8, 'NI# must be 8 digits')
    .regex(/^\d+$/, 'NI# must contain only digits'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(17, 'Max 17 characters'),
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(15, 'Max 15 characters'),
  middleInitial: z.string().max(1, 'Max 1 character').optional(),
  basicWages: z.coerce.number().min(0, 'Must be positive'),
  gratuityWages: z.coerce.number().min(0, 'Must be positive'),
  weeksWorked: z.coerce.number().min(1, 'Min 1 week').max(5, 'Max 5 weeks'),
  payIndicator: z.enum(['W', 'M']),
  category: z.coerce.number().min(1).max(7),
  isRetired: z.boolean(),
});

type EmployeeFormData = z.infer<typeof employeeSchema>;

// =============================================================================
// TYPES
// =============================================================================

interface EmployeeEditModalProps {
  /** Employee to edit (null for add mode) */
  employee: WizardEmployee | null;
  /** Whether modal is open */
  isOpen: boolean;
  /** Close callback */
  onClose: () => void;
  /** Mode: edit existing or add new */
  mode: 'edit' | 'add';
}

// =============================================================================
// CATEGORY OPTIONS
// =============================================================================

const CATEGORY_OPTIONS = [
  { value: 1, label: '1 - Regular (<65)' },
  { value: 2, label: '2 - Retiree (<65)' },
  { value: 3, label: '3 - Senior (65-69)' },
  { value: 4, label: '4 - Senior Retiree' },
  { value: 5, label: '5 - Elderly (≥70)' },
  { value: 6, label: '6 - Elderly Retiree' },
  { value: 7, label: '7 - Summer Student' },
];

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Modal for editing or adding employee details.
 */
export function EmployeeEditModal({
  employee,
  isOpen,
  onClose,
  mode,
}: EmployeeEditModalProps) {
  const { state, updateEmployee, addEmployee } = useWizard();
  const isHospitality = state.formatType === 'HOSPITALITY';

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      employeeNi: '',
      lastName: '',
      firstName: '',
      middleInitial: '',
      basicWages: 0,
      gratuityWages: 0,
      weeksWorked: 4,
      payIndicator: 'M',
      category: 1,
      isRetired: false,
    },
  });

  // Reset form when employee changes or modal opens
  useEffect(() => {
    if (isOpen) {
      if (employee && mode === 'edit') {
        reset({
          employeeNi: employee.employeeNi,
          lastName: employee.lastName,
          firstName: employee.firstName,
          middleInitial: employee.middleInitial || '',
          basicWages: employee.basicWages,
          gratuityWages: employee.gratuityWages,
          weeksWorked: employee.weeksWorked,
          payIndicator: employee.payIndicator,
          category: employee.category,
          isRetired: employee.isRetired,
        });
      } else {
        reset({
          employeeNi: '',
          lastName: '',
          firstName: '',
          middleInitial: '',
          basicWages: 0,
          gratuityWages: 0,
          weeksWorked: 4,
          payIndicator: 'M',
          category: 1,
          isRetired: false,
        });
      }
    }
  }, [employee, mode, isOpen, reset]);

  const onSubmit = (data: EmployeeFormData) => {
    if (mode === 'edit' && employee) {
      updateEmployee(employee.id, {
        employeeNi: data.employeeNi,
        lastName: data.lastName.toUpperCase(),
        firstName: data.firstName.toUpperCase(),
        middleInitial: data.middleInitial?.toUpperCase() || undefined,
        basicWages: data.basicWages,
        gratuityWages: data.gratuityWages,
        weeksWorked: data.weeksWorked,
        payIndicator: data.payIndicator,
        category: data.category,
        isRetired: data.isRetired,
      });
    } else {
      addEmployee({
        employeeNi: data.employeeNi,
        lastName: data.lastName.toUpperCase(),
        firstName: data.firstName.toUpperCase(),
        middleInitial: data.middleInitial?.toUpperCase() || undefined,
        basicWages: data.basicWages,
        gratuityWages: data.gratuityWages,
        weeksWorked: data.weeksWorked,
        payIndicator: data.payIndicator,
        category: data.category,
        isRetired: data.isRetired,
        hasNiMatch: false,
        isNew: true,
      });
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="mx-auto max-w-lg w-full bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <DialogTitle className="text-lg font-medium text-gray-900">
              {mode === 'edit' ? 'Edit Employee' : 'Add Employee'}
            </DialogTitle>
            <button
              onClick={onClose}
              type="button"
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6">
            <div className="grid grid-cols-2 gap-4">
              {/* NI Number */}
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  NI Number <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('employeeNi')}
                  type="text"
                  maxLength={8}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono"
                  placeholder="12345678"
                />
                {errors.employeeNi && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.employeeNi.message}
                  </p>
                )}
              </div>

              {/* Category */}
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  {...register('category')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  {CATEGORY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('lastName')}
                  type="text"
                  maxLength={17}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm uppercase"
                />
                {errors.lastName && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.lastName.message}
                  </p>
                )}
              </div>

              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('firstName')}
                  type="text"
                  maxLength={15}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm uppercase"
                />
                {errors.firstName && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.firstName.message}
                  </p>
                )}
              </div>

              {/* Basic Wages */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Basic Wages <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    {...register('basicWages')}
                    type="number"
                    step="0.01"
                    min="0"
                    className="block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                {errors.basicWages && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.basicWages.message}
                  </p>
                )}
              </div>

              {/* Gratuity Wages (hospitality only) */}
              {isHospitality && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Gratuity
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      {...register('gratuityWages')}
                      type="number"
                      step="0.01"
                      min="0"
                      className="block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Weeks Worked */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Weeks Worked
                </label>
                <select
                  {...register('weeksWorked')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  {[1, 2, 3, 4, 5].map(w => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>
              </div>

              {/* Pay Indicator */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Pay Frequency
                </label>
                <select
                  {...register('payIndicator')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="M">Monthly</option>
                  <option value="W">Weekly</option>
                </select>
              </div>

              {/* Is Retired */}
              <div className="col-span-2">
                <label className="flex items-center gap-2">
                  <input
                    {...register('isRetired')}
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Employee is receiving NIB retirement benefits
                  </span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {mode === 'edit' ? 'Save Changes' : 'Add Employee'}
              </button>
            </div>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
